import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Typography } from '@mui/material';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Sort, Inject } from '@syncfusion/ej2-react-grids';
import { ComplianceListItemView } from '../../../../services/ComplianceService/types/complianceTypes';
import { SectionPaper } from '../../../common/components/styled-ui-elements';
import { useGetComplianceItemsQuery } from '../../../../services/ComplianceService/complianceApi';
import {
  useGetComplianceTypesQuery,
  useGetAccreditationBodiesQuery,
  useGetComplianceStatusesQuery,
} from '../../../../services/DimensionService/dimensionApi';
import { EnumView } from '../../../../services/DimensionService/types/dimensionTypes';
import { ComplianceEmptyState } from '../shared/states';
import { ComplianceCompleteDialog } from '../ComplianceCompleteDialog';

interface ComplianceGridProps {
  searchText?: string;
}

// Five status colors, including Waiver — one more than issue #100's literal 4-color list
// (research.md #7).
const getStatusColor = (status: string): { bg: string; color: string } => {
  switch (status) {
    case 'CURRENT': return { bg: '#d1fae5', color: '#065f46' };
    case 'DUESOON': return { bg: '#fef3c7', color: '#92400e' };
    case 'OVERDUE': return { bg: '#fee2e2', color: '#991b1b' };
    case 'NOTSTARTED': return { bg: '#dbeafe', color: '#1e40af' };
    case 'WAIVER': return { bg: '#e5e7eb', color: '#374151' };
    default: return { bg: '#e5e7eb', color: '#374151' };
  }
};

// The grid's Type/Accreditation/Status columns store the same short codes used for
// filtering/color-matching (e.g. "IC", "TJC", "CURRENT"); look up each one's friendly display
// name from the matching dimension list so the grid reads like the rest of the form.
const buildDisplayNameLookup = (options: EnumView[]): Record<string, string> =>
  Object.fromEntries(options.map(o => [o.value, o.name]));

export const ComplianceGrid: React.FC<ComplianceGridProps> = ({ searchText = '' }) => {
  const navigate = useNavigate();
  const { data: items } = useGetComplianceItemsQuery();
  const { data: complianceTypes = [] } = useGetComplianceTypesQuery();
  const { data: accreditationBodies = [] } = useGetAccreditationBodiesQuery();
  const { data: statuses = [] } = useGetComplianceStatusesQuery();
  const [completeItemId, setCompleteItemId] = useState<string | null>(null);

  const typeNames = React.useMemo(() => buildDisplayNameLookup(complianceTypes), [complianceTypes]);
  const accreditationNames = React.useMemo(
    () => buildDisplayNameLookup(accreditationBodies),
    [accreditationBodies],
  );
  const statusNames = React.useMemo(() => buildDisplayNameLookup(statuses), [statuses]);

  const filteredItems = React.useMemo(() => {
    if (!items) return [];
    if (searchText.trim() === '') return items;

    const search = searchText.toLowerCase();
    return items.filter(item =>
      item.title.toLowerCase().includes(search) ||
      item.complianceType.toLowerCase().includes(search) ||
      item.centerName.toLowerCase().includes(search) ||
      item.status.toLowerCase().includes(search) ||
      item.accreditationBody.toLowerCase().includes(search)
    );
  }, [items, searchText]);

  const typeTemplate = (item: ComplianceListItemView) => typeNames[item.complianceType] || item.complianceType;

  const accreditationTemplate = (item: ComplianceListItemView) =>
    accreditationNames[item.accreditationBody] || item.accreditationBody;

  const statusTemplate = (item: ComplianceListItemView) => {
    const colors = getStatusColor(item.status);
    return (
      <span
        style={{
          display: 'inline-block',
          backgroundColor: colors.bg,
          color: colors.color,
          fontWeight: 600,
          fontSize: '0.7rem',
          padding: '2px 10px',
          borderRadius: '12px',
          lineHeight: '18px',
          whiteSpace: 'nowrap',
        }}
      >
        {statusNames[item.status] || item.status}
      </span>
    );
  };

  const actionsTemplate = (item: ComplianceListItemView) => (
    <Button
      size="small"
      variant="outlined"
      onClick={(event) => {
        event.stopPropagation();
        setCompleteItemId(item.id);
      }}
      sx={{ textTransform: 'none' }}
    >
      Mark Complete
    </Button>
  );

  const handleRowSelected = (args: any) => {
    if (args?.data?.id) {
      navigate(`/compliance/edit?id=${args.data.id}`);
    }
  };

  if (!items) {
    return <ComplianceEmptyState message="Loading compliance requirements..." />;
  }

  if (filteredItems.length === 0) {
    return (
      <ComplianceEmptyState
        message={searchText ? 'No compliance requirements found. Try adjusting your search.' : 'No compliance requirements found. Create your first requirement to get started.'}
      />
    );
  }

  return (
    <SectionPaper>
      <Typography
        variant="caption"
        sx={{
          mb: 1,
          display: 'block',
          color: '#64748b',
          fontWeight: 500
        }}
      >
        Showing {filteredItems.length} requirement{filteredItems.length !== 1 ? 's' : ''}
      </Typography>
      <GridComponent
        dataSource={filteredItems}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 20 }}
        sortSettings={{
          columns: [{ field: 'nextDueDate', direction: 'Ascending' as any }]
        }}
        height={500}
        rowHeight={36}
        gridLines="Both"
        rowSelected={handleRowSelected}
        cssClass="clickable-rows"
      >
        <ColumnsDirective>
          <ColumnDirective
            field="title"
            headerText="Title"
            width="160"
            textAlign="Left"
          />
          <ColumnDirective
            field="complianceType"
            headerText="Type"
            width="120"
            textAlign="Left"
            template={typeTemplate}
          />
          <ColumnDirective
            field="centerName"
            headerText="Center"
            width="100"
            textAlign="Left"
          />
          <ColumnDirective
            field="accreditationBody"
            headerText="Accreditation"
            width="120"
            textAlign="Left"
            template={accreditationTemplate}
          />
          <ColumnDirective
            field="nextDueDate"
            headerText="Next Due"
            width="110"
            textAlign="Left"
            format="yMd"
          />
          <ColumnDirective
            headerText="Status"
            width="100"
            textAlign="Center"
            template={statusTemplate}
          />
          <ColumnDirective
            headerText="Actions"
            width="130"
            textAlign="Center"
            template={actionsTemplate}
          />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>
      <ComplianceCompleteDialog
        complianceItemId={completeItemId}
        onClose={() => setCompleteItemId(null)}
      />
    </SectionPaper>
  );
};

export default ComplianceGrid;
